import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { ApiResponse, Crew, CrewMember, CrewTemplate, CrewTemplateSlot, PaginatedResponse } from '@/types'

export type CrewTemplatePayload = {
  service_instance: string | number
  name: string
  code?: string
  description?: string
  logo_url?: string
  job_category?: string | number | null
  active?: boolean
  slots?: CrewTemplateSlot[]
}

export type CrewPayload = {
  crew_template: string | number
  service_instance: string | number
  name: string
  code?: string
  active?: boolean
}

export type CrewMemberPayload = {
  crew: string | number
  crew_template_slot: string | number
  worker_skill: string | number
  ordering?: number
}

const unwrapList = <T>(payload: ApiResponse<PaginatedResponse<T>> | PaginatedResponse<T> | T[]): T[] => {
  if (Array.isArray(payload)) {
    return payload
  }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as ApiResponse<PaginatedResponse<T>>).data
    if (Array.isArray(data)) {
      return data
    }
    return data?.results ?? []
  }
  if (payload && typeof payload === 'object' && 'results' in payload) {
    return (payload as PaginatedResponse<T>).results ?? []
  }
  return []
}

const unwrapItem = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

export const crewsApi = {
  listTemplates: async (params?: Record<string, string | number | boolean | undefined>) => {
    const response = await api.get(API_PATHS.workers.crewTemplatesList, { params })
    return unwrapList<CrewTemplate>(response.data)
  },
  getTemplate: async (id: string | number) => {
    const response = await api.get(API_PATHS.workers.crewTemplatesGet(id))
    return unwrapItem<CrewTemplate>(response.data)
  },
  createTemplate: async (payload: CrewTemplatePayload) => {
    const response = await api.post(API_PATHS.workers.crewTemplatesCreate, payload)
    return unwrapItem<CrewTemplate>(response.data)
  },
  updateTemplate: async (id: string | number, payload: Partial<CrewTemplatePayload>) => {
    const response = await api.post(API_PATHS.workers.crewTemplatesUpdate(id), payload)
    return unwrapItem<CrewTemplate>(response.data)
  },
  deleteTemplate: async (id: string | number) => {
    await api.delete(API_PATHS.workers.crewTemplatesDelete(id))
  },
  listCrews: async (params?: Record<string, string | number | boolean | undefined>) => {
    const response = await api.get(API_PATHS.workers.crewsList, { params })
    return unwrapList<Crew>(response.data)
  },
  listReadyCrews: async (params: { crew_template_id: string | number; service_instance_id?: string | number }) => {
    const response = await api.get(API_PATHS.workers.crewsReadyList, { params })
    return unwrapList<Crew>(response.data)
  },
  getCrew: async (id: string | number) => {
    const response = await api.get(API_PATHS.workers.crewsGet(id))
    return unwrapItem<Crew>(response.data)
  },
  createCrew: async (payload: CrewPayload) => {
    const response = await api.post(API_PATHS.workers.crewsCreate, payload)
    return unwrapItem<Crew>(response.data)
  },
  updateCrew: async (id: string | number, payload: Partial<CrewPayload>) => {
    const response = await api.post(API_PATHS.workers.crewsUpdate(id), payload)
    return unwrapItem<Crew>(response.data)
  },
  deleteCrew: async (id: string | number) => {
    await api.delete(API_PATHS.workers.crewsDelete(id))
  },
  createMember: async (payload: CrewMemberPayload) => {
    const response = await api.post(API_PATHS.workers.crewMembersCreate, payload)
    return unwrapItem<CrewMember>(response.data)
  },
  updateMember: async (id: string | number, payload: Partial<CrewMemberPayload>) => {
    const response = await api.post(API_PATHS.workers.crewMembersUpdate(id), payload)
    return unwrapItem<CrewMember>(response.data)
  },
  deleteMember: async (id: string | number) => {
    await api.delete(API_PATHS.workers.crewMembersDelete(id))
  },
  assignToJob: async (payload: { job_id: string | number; crew_id: string | number }) => {
    const response = await api.post(API_PATHS.workers.crewsAssignToJob, payload)
    return unwrapItem<{ assigned_count: number; job_id: string | number; crew_id: string | number }>(response.data)
  },
}

export default crewsApi
